'use client';

import { Box, Grid, Stack, Typography } from '@/primitives';
import { useNewsFeed } from '@/hooks/publicSearch/news/useNewsFeed';
import NewsCard from './NewsCard';
import { blue, slate } from '@/lib/colours';
import NewsCardSkeleton from './NewsCardSkeleton';
import { LANDING_MAX_WIDTH_PX, sectionShell } from './homeLandingSectionSx';

export default function NewsSection() {
  const { data, isLoading } = useNewsFeed();

  return (
    <Box
      component="section"
      aria-labelledby="landing-news-title"
      sx={{
        py: { xs: 8, md: 10 },
        backgroundColor: slate[950],
        borderTop: `1px solid ${slate[800]}`,
      }}
    >
      <Box
        sx={{
          ...sectionShell,
          maxWidth: `${LANDING_MAX_WIDTH_PX}px`,
        }}
      >
        <Stack
          spacing={0.5}
          sx={{
            alignItems: 'center',
            mb: { xs: 3, md: 4 },
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Typography
            color={slate[50]}
            component="h2"
            id="landing-news-title"
            sx={{ textAlign: 'center' }}
            variant="headlinelarge"
          >
            Latest news
          </Typography>
          <Typography
            color={blue[300]}
            component="p"
            sx={{ textAlign: 'center' }}
            variant="titlemedium"
          >
            From ABRI
          </Typography>
        </Stack>
        <Grid
          container
          columnSpacing={{ xs: '20px', md: '24px' }}
          rowSpacing={{ xs: '32px', md: '28px' }}
        >
          {isLoading ? (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <NewsCardSkeleton landing />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <NewsCardSkeleton landing />
              </Grid>
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  flexDirection: 'column',
                  gap: '24px',
                  mb: { sm: '16px', md: 'unset' },
                }}
              >
                <NewsCardSkeleton isBrief landing />
                <NewsCardSkeleton isBrief landing />
                <NewsCardSkeleton isBrief landing />
              </Grid>
            </>
          ) : (
            <>
              {data?.[0] && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <NewsCard
                    date={
                      data[0].isoDate ? new Date(data[0].isoDate) : undefined
                    }
                    imgUrl={data[0].mediaContent.url ?? ''}
                    landing
                    referenceLink={data[0].link ?? ''}
                    snippet={data[0].contentSnippet ?? ''}
                    title={data[0].title ?? ''}
                  />
                </Grid>
              )}
              {data?.[1] && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <NewsCard
                    date={
                      data[1].isoDate ? new Date(data[1].isoDate) : undefined
                    }
                    imgUrl={data[1].mediaContent.url ?? ''}
                    landing
                    referenceLink={data[1].link ?? ''}
                    snippet={data[1].contentSnippet ?? ''}
                    title={data[1].title ?? ''}
                  />
                </Grid>
              )}
              {data?.[2] && (
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    gap: '24px',
                    mb: { sm: '16px', md: 'unset' },
                  }}
                >
                  <NewsCard
                    date={
                      data[2].isoDate ? new Date(data[2].isoDate) : undefined
                    }
                    imgUrl={data[2].mediaContent.url ?? ''}
                    isBrief
                    landing
                    referenceLink={data[2].link ?? ''}
                    snippet={data[2].contentSnippet ?? ''}
                    title={data[2].title ?? ''}
                  />
                  {data?.[3] && (
                    <NewsCard
                      date={
                        data[3].isoDate ? new Date(data[3].isoDate) : undefined
                      }
                      imgUrl={data[3].mediaContent.url ?? ''}
                      isBrief
                      landing
                      referenceLink={data[3].link ?? ''}
                      snippet={data[3].contentSnippet ?? ''}
                      title={data[3].title ?? ''}
                    />
                  )}
                  {data?.[4] && (
                    <NewsCard
                      date={
                        data[4].isoDate ? new Date(data[4].isoDate) : undefined
                      }
                      imgUrl={data[4].mediaContent.url ?? ''}
                      isBrief
                      landing
                      referenceLink={data[4].link ?? ''}
                      snippet={data[4].contentSnippet ?? ''}
                      title={data[4].title ?? ''}
                    />
                  )}
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
